import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { css } from "@emotion/css";
import { useCoachingRequest } from "../../hooks/useCoachingRequest";
import {
  useCoachingSessionStore,
  useCoachingSessionState,
  useIsStreaming,
  useCoachingError,
  nonSupersededCount,
} from "../../stores/coachingSessionStore";
import type { UiInteractionValue, CoachingSessionEvent } from "../../types/events";
import type { OfferCard } from "../../types/state";
import type { EmissionAck, EmissionReceipt } from "@sharpana/session-api/types";
import { commitOffer } from "../../data/offers";
import { PhaseHeader } from "./PhaseHeader";
import { SessionProgress } from "./SessionProgress";
import { WorkLane } from "./WorkLane";
import { CraftedSessionContext } from "./craftedSessionContext";
import { AskModal } from "./AskModal";
import { SessionWrapOverlay } from "./SessionWrapOverlay";
import { TermPreviewOverlay } from "./TermPreviewOverlay";
import { ScopeBreadcrumb } from "./ScopeBreadcrumb";
import { DeepDiveView } from "./DeepDiveView";

// Bind to the (dynamic) visual viewport height rather than ``100%``: the
// app shell doesn't set a height on html/body/#root, so ``height: 100%``
// would collapse to content height and the bottom help bar would float
// with the content.  ``--app-100vh`` is the robust full-viewport height
// (kept in sync by initViewportHeightVar), so the lane scrolls internally
// and the help bar stays pinned at the bottom.
const pageStyle = css`
  display: flex;
  flex-direction: column;
  height: var(--app-100vh, 100vh);
  max-height: var(--app-100vh, 100vh);
  max-width: 720px;
  margin: 0 auto;
  background: var(--color-bg);
  color: var(--color-text);
`;

// An authored activity (the runtime engine) is a bench with figures, not a
// chat column: on desktop it gets a wider page, and the activity's own
// reading measure widens with it (@zairai/design's --zai-measure).
const pageWideStyle = css`
  @media (min-width: 1000px) {
    max-width: 1040px;
  }
`;

const loadingStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--app-100vh, 100vh);
  color: var(--color-text-muted);
  font-size: 1.1em;
`;

const errorStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--app-100vh, 100vh);
  color: var(--color-error);
  font-size: 1.1em;
  padding: 24px;
  text-align: center;
`;

interface CoachingSessionPageProps {
  /**
   * Which session engine this page drives — REQUIRED, so every route
   * names its engine explicitly.  "generative" is the legacy
   * LLM-materialized engine; "runtime" is the Session-API runtime
   * (authored activities served from a pinned spec).  Same shell, same
   * scope-tree state shape — only the WS action names and the start
   * payload differ.
   */
  engine: "generative" | "runtime";
}

const WS_ACTIONS = {
  generative: {
    start: "generative_session_start",
    get: "generative_session_get",
    dispatch: "generative_session_dispatch",
  },
  runtime: {
    start: "runtime_session_start",
    get: "runtime_session_get",
    dispatch: "runtime_session_dispatch",
  },
} as const;

export const CoachingSessionPage: React.FC<CoachingSessionPageProps> = ({
  engine,
}) => {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { sendCoachingAction } = useCoachingRequest();
  const actions = WS_ACTIONS[engine];
  const sessionState = useCoachingSessionState();
  const isStreaming = useIsStreaming();
  const error = useCoachingError();
  const reset = useCoachingSessionStore((s) => s.reset);
  // The Ask thread is FE-local UX (not a server overlay): opening it must
  // never disturb the work-lane step or an in-flight wrap.
  const [askOpen, setAskOpen] = useState(false);
  // The last priced close, for the ledger tick.  Page-local because it is
  // a MOMENT, not state: the server has no notion of "the receipt that is
  // currently showing", and should not.
  const [receipt, setReceipt] = useState<EmissionReceipt | null>(null);
  // A commit is in flight; the wrap card's cards go inert until it lands
  // so a second tap cannot start a second navigation.
  const [takingOffer, setTakingOffer] = useState(false);
  // The term the learner just asked about, held only until the real
  // overlay arrives.  ``preview_term`` streams with no snapshot ahead of
  // it, so the overlay's shell is not written until the model's first
  // token — seconds, on a reasoning model, in which the tap produced
  // nothing at all on screen.  Naming the term here lets the modal open
  // on the tap and fill in after.
  const [previewingTerm, setPreviewingTerm] = useState<string | null>(null);

  // ...and it stands down the moment anything real supersedes it: the
  // overlay the server sends, or a navigation (going deeper opens a new
  // scope).  Watching both means the stand-in can never outlive the one
  // request it stood in for, whichever way that request ends.
  // Watched as plain strings, not the overlay object: every streamed
  // delta clones the state, so watching identity would clear the
  // stand-in on the first token of its own definition.
  const serverOverlayKind = sessionState?.overlay?.kind;
  const activeScopeId = sessionState?.activeScopeId;
  useEffect(() => {
    setPreviewingTerm(null);
  }, [serverOverlayKind, activeScopeId]);

  // Start or resume session on mount
  useEffect(() => {
    reset();

    if (sessionId) {
      // Resume existing session
      sendCoachingAction(actions.get, { sessionId });
    } else if (engine === "runtime") {
      // Start a crafted session on a published activity or catalog unit:
      // /coaching/activity?ref=zai:attn/bench@1#c2[&seed=7] pins one
      // unit; /coaching/activity?recipe=default_session[&planet=…&minutes=…]
      // hands the choosing to a recipe.  The second is how a chooser-led
      // sitting is started before the dealer's cards are on the home, and
      // it stays afterwards as the dev affordance for driving one recipe
      // on purpose.
      const ref = searchParams.get("ref");
      const recipe = searchParams.get("recipe");
      if (ref || recipe) {
        const payload: Record<string, string | number> = {};
        if (ref) payload.activityRef = ref;
        if (recipe) payload.recipe = recipe;
        for (const key of ["planet", "claim", "flavor", "mode"]) {
          const value = searchParams.get(key);
          if (value) payload[key] = value;
        }
        const minutes = searchParams.get("minutes");
        if (minutes) payload.minutes = Number(minutes);
        const seed = searchParams.get("seed");
        if (seed) payload.seed = Number(seed);
        sendCoachingAction(actions.start, payload);
      }
    } else {
      // Start new session.  Superuser-only ?cloneFrom=<sessionId>
      // tells the backend to deep-copy an existing coach session's
      // state into a new one for reproducing / replaying from a
      // snapshot.  When cloning, arcId/launchMode aren't required
      // — the source session's arc carries over.
      const cloneFrom = searchParams.get("cloneFrom");
      if (cloneFrom) {
        sendCoachingAction(actions.start, {
          cloneFromSessionId: cloneFrom,
        });
        return;
      }
      const arcId = searchParams.get("arcId");
      const launchMode = searchParams.get("launchMode") || "regular";
      if (arcId) {
        // Optional per-session overrides: ``?recipe=`` (pedagogy) and
        // ``?hook=`` (how the session opens — direct / resonant_framings /
        // resonant_paths).  Omitted → env defaults.
        const payload: Record<string, string> = { arcId, launchMode };
        const recipe = searchParams.get("recipe");
        const hook = searchParams.get("hook");
        if (recipe) payload.recipeId = recipe;
        if (hook) payload.hookStrategy = hook;
        sendCoachingAction(actions.start, payload);
      }
    }
  }, [sessionId]);

  const setPendingSubmission = useCoachingSessionStore((s) => s.setPendingSubmission);

  const handleSubmitInteraction = useCallback(
    (interactionId: string, value: UiInteractionValue, displayText: string) => {
      if (!sessionState || isStreaming) return;

      // Snapshot the active interaction as context — what the learner was
      // looking at when they took this action.  Order is {context,
      // client_event} so a reader (human or LLM) sees the situation
      // before the action.
      const activeScope = sessionState.scopes[sessionState.activeScopeId];
      const activeId = activeScope?.work?.activeInteractionId;
      const interaction = activeId ? activeScope.work.interactions[activeId] : undefined;

      const eventId = crypto.randomUUID();
      // Optimistic: show the learner bubble immediately.  Server echo
      // overwrites submission.displayText with the canonical value
      // produced by the kind's format_submission.
      setPendingSubmission(interactionId, { eventId, displayText });
      sendCoachingAction(actions.dispatch, {
        sessionId: sessionState.sessionId,
        event: {
          eventId,
          sessionId: sessionState.sessionId,
          scopeId: sessionState.activeScopeId,
          clientTimestamp: new Date().toISOString(),
          event: {
            type: "submit_response",
            payload: {
              context: { interaction },
              client_event: { interactionId, value },
            },
          },
        },
      });
    },
    [sessionState, isStreaming, sendCoachingAction, setPendingSubmission]
  );

  const handleCompleteSession = useCallback(() => {
    if (!sessionState) return;

    // complete_session fires from the session_wrap overlay — capture
    // whatever's currently on screen (overlay or active interaction).
    const activeScope = sessionState.scopes[sessionState.activeScopeId];
    const activeId = activeScope?.work?.activeInteractionId;
    const interaction =
      sessionState.overlay ??
      (activeId ? activeScope?.work.interactions[activeId] : undefined);

    const eventId = crypto.randomUUID();
    sendCoachingAction(actions.dispatch, {
      sessionId: sessionState.sessionId,
      event: {
        eventId,
        sessionId: sessionState.sessionId,
        scopeId: sessionState.activeScopeId,
        clientTimestamp: new Date().toISOString(),
        event: {
          type: "complete_session",
          payload: { context: { interaction }, client_event: {} },
        },
      },
    });

    // Navigate home after completion
    setTimeout(() => navigate("/coaching"), 500);
  }, [sessionState, sendCoachingAction, navigate]);

  // Shared envelope builder for the help actions (Hint / Ask / Regenerate).
  // Snapshots the active work-lane interaction as context, same as
  // submit_response, so the event log stays self-contained.
  const dispatchHelpEvent = useCallback(
    (event: CoachingSessionEvent) => {
      if (!sessionState || isStreaming) return;
      const activeScope = sessionState.scopes[sessionState.activeScopeId];
      const activeId = activeScope?.work?.activeInteractionId;
      const interaction = activeId ? activeScope.work.interactions[activeId] : undefined;
      if (event.payload && typeof event.payload === "object" && "context" in event.payload) {
        (event.payload as { context?: { interaction?: unknown } }).context = { interaction };
      }
      sendCoachingAction(actions.dispatch, {
        sessionId: sessionState.sessionId,
        event: {
          eventId: crypto.randomUUID(),
          sessionId: sessionState.sessionId,
          scopeId: sessionState.activeScopeId,
          clientTimestamp: new Date().toISOString(),
          event,
        },
      });
    },
    [sessionState, isStreaming, sendCoachingAction],
  );

  const handleRevealHint = useCallback(() => {
    // Hints are step-scoped: they stream into the active step's inline
    // HintCard (not the chat thread), so this just dispatches — no modal.
    dispatchHelpEvent({ type: "request_hint", payload: { client_event: { value: {} } } });
  }, [dispatchHelpEvent]);

  const handleAskSubmit = useCallback(
    (value: { kind: "text"; text: string } | { kind: "relevance" }) => {
      dispatchHelpEvent({ type: "ask_submit", payload: { client_event: { value } } });
    },
    [dispatchHelpEvent],
  );

  const handleRegenerate = useCallback(
    (button: "too_easy" | "too_hard") => {
      // Close the Ask modal so the freshly recalibrated step is in view.
      setAskOpen(false);
      dispatchHelpEvent({ type: "regenerate_step", payload: { client_event: { value: { button } } } });
    },
    [dispatchHelpEvent],
  );

  // Navigation events (scope switching / closing the preview) may fire
  // mid-stream — they supersede the in-flight generation rather than being
  // blocked by it.
  const dispatchNav = useCallback(
    (event: CoachingSessionEvent) => {
      if (!sessionState) return;
      sendCoachingAction(actions.dispatch, {
        sessionId: sessionState.sessionId,
        event: {
          eventId: crypto.randomUUID(),
          sessionId: sessionState.sessionId,
          scopeId: sessionState.activeScopeId,
          clientTimestamp: new Date().toISOString(),
          event,
        },
      });
    },
    [sessionState, sendCoachingAction],
  );

  // Highlight a term → stream its definition into the preview modal.
  const handlePreviewTerm = useCallback(
    (text: string) => {
      setAskOpen(false);
      setPreviewingTerm(text);
      dispatchHelpEvent({ type: "preview_term", payload: { client_event: { selectedText: text } } });
    },
    [dispatchHelpEvent],
  );

  // From the preview modal: open a nested deep-dive scope on the term.
  const handleGoDeeper = useCallback(
    (term: string) => {
      dispatchHelpEvent({ type: "dive_deeper", payload: { client_event: { selectedText: term } } });
    },
    [dispatchHelpEvent],
  );

  const handleCloseOverlay = useCallback(
    (overlayId: string) => {
      dispatchNav({ type: "close_overlay", payload: { client_event: { overlayId } } });
    },
    [dispatchNav],
  );

  const handleNavigateScope = useCallback(
    (targetScopeId: string) => {
      dispatchNav({ type: "navigate_scope", payload: { client_event: { targetScopeId } } });
    },
    [dispatchNav],
  );

  const handleConceptMore = useCallback(() => {
    dispatchHelpEvent({ type: "concept_turn", payload: { client_event: { value: { mode: "more" } } } });
  }, [dispatchHelpEvent]);

  const handleConceptAsk = useCallback(
    (text: string) => {
      dispatchHelpEvent({ type: "concept_turn", payload: { client_event: { value: { mode: "ask", text } } } });
    },
    [dispatchHelpEvent],
  );

  // Authored activities emit over their own HTTP channel, so the page
  // learns from the ACK rather than from its own dispatch.  `ready` means
  // the served instrument is finished and the host now has a way on to
  // draw; `advanced` means the route actually moved.  Both want a fresh
  // snapshot — only the second changes what is mounted, which is exactly
  // why the serve seam keeps them apart.
  const handleEmissionAck = useCallback(
    (ack: EmissionAck) => {
      if (!sessionState) return;
      // The tick is set from the ack because the ack is the only place
      // "what was NEW here" exists — a crossing read off a later snapshot
      // cannot be told from one earned last week.
      if (ack.receipt) setReceipt(ack.receipt);
      if (ack.ready || ack.advanced || ack.sessionStatus === "completed") {
        sendCoachingAction(actions.get, { sessionId: sessionState.sessionId });
      }
    },
    [sessionState, sendCoachingAction, actions],
  );

  // The seam handed to a mounted activity: how an emission ack reaches
  // the page, and how one of the activity's own glossed terms reaches
  // the coach.  Null on the generative engine, which mounts no activity.
  const craftedContext = useMemo(
    () =>
      engine === "runtime"
        ? { onEmissionAck: handleEmissionAck, onDiveDeeper: handleGoDeeper }
        : null,
    [engine, handleEmissionAck, handleGoDeeper],
  );

  // The learner said the pitch is wrong.  A press, not a setting: the
  // server records it and the RECIPE decides what it means.
  const handleCalibrate = useCallback(
    (direction: "too_hard" | "too_easy") => {
      setAskOpen(false);
      dispatchHelpEvent({ type: "session_calibrate", payload: { client_event: { direction } } });
    },
    [dispatchHelpEvent],
  );

  // "Wrap up for now" — end the sitting properly rather than abandoning
  // a tab, so the learner still gets the receipt they earned.
  const handleWrapUp = useCallback(() => {
    dispatchNav({ type: "session_wrap_now", payload: { client_event: {} } });
  }, [dispatchNav]);

  // Take one of the dealer's cards: commit it, then go to the sitting it
  // minted.  Exactly-once server-side, so a double tap navigates twice to
  // the same place rather than starting two sittings.
  const handleTakeOffer = useCallback(
    async (card: OfferCard) => {
      const offer = sessionState?.overlay?.kind === "session_wrap"
        ? sessionState.overlay.offer
        : null;
      if (!offer || takingOffer) return;
      setTakingOffer(true);
      try {
        const nextSessionId = await commitOffer(offer.offerId, card.choiceId);
        // Every card mints a RUNTIME sitting, so it opens on the runtime
        // route — the generative route would load the wrong shell.
        navigate(`/coaching/activity/${nextSessionId}`);
      } catch (commitError) {
        // An expired offer or a recipe that has since run dry: say so and
        // leave the card standing rather than silently doing nothing.
        console.error("[offer] commit failed", commitError);
        setTakingOffer(false);
      }
    },
    [sessionState, takingOffer, navigate],
  );

  // The learner pressed a server-declared work-lane control.  Today that
  // is the way on from a finished instrument; the crafted route moves
  // here and nowhere else.  Through ``dispatchNav`` because that is what
  // this is — moving between units, not answering within one.
  const handleBottomBarAction = useCallback(
    (actionId: string) => {
      // Cast because the id is server-declared and so arrives as a plain
      // string; the union it lands in is the wire contract it must match.
      dispatchNav({ type: actionId, payload: { client_event: {} } } as CoachingSessionEvent);
    },
    [dispatchNav],
  );

  if (error) {
    return (
      <div className={errorStyle}>
        Error: {error.message} ({error.code})
      </div>
    );
  }

  if (!sessionState) {
    return <div className={loadingStyle}>Loading session...</div>;
  }

  // Find active phase
  const activePhase = sessionState.phases.find((p) => p.status === "active");
  const activeScope = sessionState.scopes[sessionState.activeScopeId];

  // Plan-driven named-phase strip (Hook -> Frame -> Build -> Prove ->
  // Wrap).  The current phase index tracks the active interaction's
  // position in the work lane; the wrap overlay (or a completed
  // session) lights the terminal phase.
  const sessionPlan = sessionState.sessionPlan;
  const atWrap =
    sessionState.overlay?.kind === "session_wrap" ||
    sessionState.status === "completed";
  // Progress tracks the active interaction's position, EXCLUDING
  // superseded versions so a "feels too easy / hard" re-do is net-zero
  // (it doesn't advance the phase strip or the wrap boundary).
  const order = activeScope?.work?.interactionOrder ?? [];
  const interactions = activeScope?.work?.interactions ?? {};
  const currentPhaseIndex = Math.max(0, nonSupersededCount(order, interactions) - 1);

  // A deep-dive scope swaps the whole frame: breadcrumb chrome, an
  // explanation thread, and the Back · Ask · More control set.
  const isDeepDive = activeScope?.kind === "deep_dive";
  const overlay = sessionState.overlay;

  // How much of the sitting is left, in the crafter's own words.  Only
  // the runtime engine sends it; the generative strip keeps its counter.
  const servedInteraction = activeScope?.work?.activeInteractionId
    ? activeScope.work.interactions[activeScope.work.activeInteractionId]
    : undefined;
  const remainingWords =
    engine === "runtime" && servedInteraction?.kind === "authored_activity"
      ? servedInteraction.display?.remaining
      : undefined;

  return (
    <CraftedSessionContext.Provider
      value={craftedContext}
    >
    <div className={engine === "runtime" ? `${pageStyle} ${pageWideStyle}` : pageStyle} data-testid="coaching-session-page">
      {isDeepDive ? (
        <ScopeBreadcrumb
          scopes={sessionState.scopes}
          activeScopeId={sessionState.activeScopeId}
          onNavigate={handleNavigateScope}
        />
      ) : sessionPlan && sessionPlan.phases.length > 0 ? (
        <SessionProgress
          plan={sessionPlan}
          currentIndex={currentPhaseIndex}
          atWrap={atWrap}
          remainingWords={remainingWords}
        />
      ) : (
        activePhase && <PhaseHeader phase={activePhase} />
      )}

      {activeScope && (isDeepDive ? (
        <DeepDiveView
          scope={activeScope}
          isStreaming={isStreaming}
          onBack={() =>
            handleNavigateScope(activeScope.parentScopeId ?? sessionState.activeScopeId)
          }
          onMore={handleConceptMore}
          onAsk={handleConceptAsk}
          onDiveDeeper={handlePreviewTerm}
        />
      ) : (
        <WorkLane
          work={activeScope.work}
          isStreaming={isStreaming}
          onSubmitInteraction={handleSubmitInteraction}
          onRevealHint={handleRevealHint}
          onOpenAsk={() => setAskOpen(true)}
          onDiveDeeper={handlePreviewTerm}
          onBottomBarAction={handleBottomBarAction}
          onWrapUp={engine === "runtime" && !atWrap ? handleWrapUp : undefined}
          receipt={receipt}
        />
      ))}

      {askOpen && !isDeepDive && activeScope && (
        <AskModal
          ask={activeScope.ask}
          streaming={isStreaming}
          onClose={() => setAskOpen(false)}
          onSendText={(text) => handleAskSubmit({ kind: "text", text })}
          onAskRelevance={() => handleAskSubmit({ kind: "relevance" })}
          onRegenerate={engine === "runtime" ? handleCalibrate : handleRegenerate}
        />
      )}

      {overlay?.kind === "term_preview" ? (
        <TermPreviewOverlay
          term={overlay.term ?? ""}
          definition={overlay.definition ?? ""}
          streaming={isStreaming}
          onGoDeeper={() => handleGoDeeper(overlay.term ?? "")}
          onClose={() => handleCloseOverlay(overlay.interactionId)}
        />
      ) : previewingTerm && isStreaming ? (
        // The same modal, opened on the tap and waiting: an empty
        // ``definition`` is already how it says "Defining…".  Gated on
        // ``isStreaming`` so it cannot outlive the request that opened
        // it — if the definition never comes, this goes with it.  It has
        // no interaction to close yet, so Close just drops it.
        <TermPreviewOverlay
          term={previewingTerm}
          definition=""
          streaming
          onGoDeeper={() => handleGoDeeper(previewingTerm)}
          onClose={() => setPreviewingTerm(null)}
        />
      ) : null}

      {overlay?.kind === "session_wrap" && (
        <SessionWrapOverlay
          overlay={overlay}
          onComplete={handleCompleteSession}
          onTakeOffer={engine === "runtime" ? handleTakeOffer : undefined}
          onOtherWaters={() => navigate("/coaching/atlas")}
          busy={takingOffer}
        />
      )}
    </div>
    </CraftedSessionContext.Provider>
  );
};
